import {produce} from "immer";

import {notify} from "@client/features/notifications";
import {getProfile} from "@client/features/orm";

import * as utils from "@client/utils";

import * as hardware from "@client/hardware";
import type {Action, AppDispatch, GetState, RootState} from "@client/store";
import {Schema, models, server} from "@reactivated";

export type Actions =
    | {
          type: "CREATE_JOURNAL_ENTRIES_SUCCESS";
          entries: SearchResult[];
          count: number;
          meal_mode?: "AUTO_CREATED" | "MANUAL";
          journal_mode?: "VISION" | "VOICE" | "BARCODE" | "FACTS" | "SEARCH";
          lastUpdatedMeal?: {name: string; uuid: UUID} | null;
      }
    | {
          type: "SEARCH_LOADED";
          search: models["journal.nutrition.Search"];
          request_time: number;
          from_cache: boolean;
      }
    | {
          type: "SEARCH_LOADING";
      }
    | {
          type: "SEARCHED";
          upc?: string;
          keywords?: string;
          usda: boolean;
      }
    | {
          type: "FETCH_SEARCH_REQUEST";
      }
    | {
          type: "FETCH_SEARCH_SUCCESS";
          results: SearchResult[];
      }
    | {
          type: "FETCH_SEARCH_ERROR";
      }
    | {
          type: "CHANGE_SEARCH_FILTER";
          filter: string;
      }
    | {
          type: "CHANGE_LAST_USED_STORE";
          store: keyof Stores;
      }
    | {
          type: "CREATED_EXERCISE_TYPE";
          exerciseType: server.journal.workouts.ExerciseType.output;
      }
    | {
          type: "CREATED_QUESTION_TYPE";
          questionType: server.journal.workouts.QuestionType.output;
      }
    | {
          type: "SET_WORKOUTS";
          workouts: Schema["rpc_workouts_output"];
      }
    | {
          type: "START_SCANNING";
      }
    | {
          type: "CANCEL_SCANNING";
      }
    | {
          type: "ERROR_SCANNING";
      }
    | {
          type: "DONE_SCANNING";
      }
    | {
          type: "NO_RESULTS";
      }
    | {
          type: "ENABLE_BULK_MODE";
      }
    | {
          type: "DISABLE_BULK_MODE";
      }
    | {
          type: "RESET_ENTRY_CREATION_STREAK";
      }
    | {
          type: "CLEAR_FILTER_ON_NEXT_DELETION";
      }
    | {
          type: "CHANGE_USDA";
          value: boolean;
      }
    | {
          type: "COLLAPSE_MEAL";
          mealUuid: UUID;
      }
    | {
          type: "EXPAND_MEAL";
          mealUuid: UUID;
      }
    | {
          type: "SET_LAST_UPDATED_MEAL";
          mealUuid: UUID | null;
      };

export type SearchResult = models["journal.nutrition.SearchResult"];

/*
export interface SearchResult {
    entry: server.journal.nutrition.ExpandedEntry.output;
    name: string;
    backend_id: string | number;
    backend: models["journal.nutrition.SearchResult"]["backend"];
    source: string;
    isSelected?: boolean;
    isCupboard?: boolean;
}
    */

interface Stores {
    database: SearchResult[];
    foods: SearchResult[];
    recent: SearchResult[];
    recipes: SearchResult[];
}

const initialState = {
    predictions: null as models["journal.predictions.MealPredictions"] | null,
    entryCreationStreak: 0,
    hasNeverSearched: true,
    usda: false,
    clearFilterOnNextDeletion: false,
    filter: "",
    isSearching: false,
    isBulkEnabled: false,
    lastAddedFoods: null as {
        meal: string;
        sources: string[];
    } | null,
    foodUser: [] as server.journal.nutrition.FoodUser.output[],
    isLoaded: "NOT_LOADED" as "NOT_LOADED" | "LOADED" | "LOADING",
    isMatchServingsEnabled: false,
    isScanning: false,
    lastUsedStore: "recent" as keyof Stores,
    workouts: {
        data: null as Schema["rpc_workouts_output"] | null,
    },
    stores: {
        database: [],
        foods: [],
        recent: [],
        recipes: [],
    } as Stores,
    manualMealExpansions: {} as {[mealUuid: UUID]: boolean},
    lastUpdatedMealUuid: null as string | null,
};

export const reducer = (state = initialState, action: Action): typeof initialState => {
    switch (action.type) {
        case "LOGOUT":
            return initialState;
        case "CREATED_EXERCISE_TYPE":
            return produce(state, (draftState) => {
                draftState.workouts.data?.exercise_types.unshift(action.exerciseType);
            });
        case "CREATED_QUESTION_TYPE":
            return produce(state, (draftState) => {
                draftState.workouts.data?.question_types.unshift(action.questionType);
            });
        case "SET_WORKOUTS":
            return produce(state, (draftState) => {
                draftState.workouts.data = action.workouts;
            });
        case "START_SCANNING":
            return utils.assign(state, {isScanning: true});
        case "CLEAR_FILTER_ON_NEXT_DELETION":
            return {...state, clearFilterOnNextDeletion: true};
        case "ERROR_SCANNING":
        case "DONE_SCANNING":
        case "CANCEL_SCANNING":
            return utils.assign(state, {isScanning: false});
        case "RESET_ENTRY_CREATION_STREAK":
            return utils.assign(state, {entryCreationStreak: 0});
        case "CHANGE_USDA":
            return utils.assign(state, {usda: action.value});
        case "COLLAPSE_MEAL":
            return produce(state, (draftState) => {
                draftState.manualMealExpansions[action.mealUuid] = false;
            });
        case "EXPAND_MEAL":
            return produce(state, (draftState) => {
                draftState.manualMealExpansions[action.mealUuid] = true;
            });
        case "SET_LAST_UPDATED_MEAL":
            return produce(state, (draftState) => {
                draftState.lastUpdatedMealUuid = action.mealUuid;
            });

        // For recent entries created through the journal we optimize.
        case "CREATE_JOURNAL_ENTRIES_SUCCESS": {
            const resultsToRemove = action.entries.map(
                (entry) => `${entry.backend}-${entry.backend_id}`,
            );

            return produce(state, (draftState) => {
                const removed = state.stores.recent.filter(
                    (entry) =>
                        !resultsToRemove.includes(
                            `${entry.backend}-${entry.backend_id}`,
                        ),
                );
                draftState.stores.recent = action.entries
                    .filter((entry) => entry.backend != "inline")
                    .concat(removed);
                draftState.entryCreationStreak += 1;
                draftState.filter = "";

                if (action.lastUpdatedMeal != null) {
                    if (
                        draftState.lastAddedFoods?.meal !== action.lastUpdatedMeal.name
                    ) {
                        draftState.lastAddedFoods = {
                            meal: action.lastUpdatedMeal.name,
                            sources: action.entries.map((entry) => entry.source),
                        };
                    } else {
                        draftState.lastAddedFoods.sources =
                            draftState.lastAddedFoods.sources.concat(
                                action.entries.map((entry) => entry.source),
                            );
                    }

                    // Set the last updated meal for auto-expansion
                    draftState.lastUpdatedMealUuid = action.lastUpdatedMeal.uuid;
                }
            });
        }
        case "ENABLE_BULK_MODE": {
            return utils.assign(state, {isBulkEnabled: true});
        }
        case "DISABLE_BULK_MODE": {
            return utils.assign(state, {isBulkEnabled: false});
        }
        case "CHANGE_LAST_USED_STORE": {
            return {...state, lastUsedStore: action.store};
        }
        case "SEARCH_LOADING": {
            return {...state, isLoaded: "LOADING"};
        }
        case "SEARCH_LOADED": {
            return produce(state, (draftState) => {
                draftState.isLoaded = "LOADED";
                draftState.foodUser = action.search.food_user;
                draftState.stores.recent = action.search.recent;
                draftState.stores.recipes = action.search.recipes;
                draftState.stores.foods = action.search.foods;
                draftState.predictions = action.search.predictions;
            });
        }
        case "FETCH_SEARCH_ERROR": {
            const stores = Object.assign({}, state.stores, {database: []});
            return Object.assign({}, state, {stores, isSearching: false});
        }
        case "FETCH_SEARCH_SUCCESS": {
            const stores = Object.assign({}, state.stores, {database: action.results});
            return Object.assign({}, state, {stores, isSearching: false});
        }
        case "CHANGE_SEARCH_FILTER": {
            // If we are deleting characters and have just selected an entry,
            // it means the user is deleting everything to filter for
            // something else in bulk mode.
            const updatedFilter =
                state.clearFilterOnNextDeletion === true &&
                action.filter === state.filter.slice(0, -1)
                    ? ""
                    : action.filter;

            return Object.assign({}, state, {
                clearFilterOnNextDeletion: false,
                filter: updatedFilter,
            });
        }
        case "FETCH_SEARCH_REQUEST":
            return {...state, hasNeverSearched: false, isSearching: true};
        default: {
            return state;
        }
    }
};

export const fetchSearch =
    (inputParams: {upc?: string; keywords?: string}) =>
    async (dispatch: AppDispatch, getState: GetState) => {
        dispatch({
            type: "FETCH_SEARCH_REQUEST",
        });
        const state = getState();

        const source = state.search.usda ? "USDA" : "ALL";

        dispatch({
            type: "SEARCHED",
            ...inputParams,
            usda: state.search.usda,
        });

        const result = await server.journal.nutrition.search({
            upc: inputParams.upc ?? "",
            keywords: inputParams.keywords ?? "",
            source,
        });

        if (result.type == "success") {
            dispatch({
                type: "FETCH_SEARCH_SUCCESS",
                results: result.data,
            });
            return result.data;
        } else {
            dispatch(
                notify(
                    `Our food database is currently undergoing maintenance. Please try again in a few minutes.`,
                    {type: "error", permanent: true},
                ),
            );
            dispatch({
                type: "FETCH_SEARCH_ERROR",
            });
            return [];
        }
    };

export const {loadSearch} = new (class {
    outstanding: Promise<void> | null = null;

    loadSearch =
        ({reset}: {reset?: boolean} = {}) =>
        async (dispatch: AppDispatch, getState: GetState) => {
            const state = getState();
            const cache =
                await hardware.getCache<models["journal.nutrition.Search"]>(
                    "search-cache",
                );

            if (reset == true || state.search.isLoaded === "NOT_LOADED") {
                this.outstanding = null;
            }

            if (this.outstanding == null) {
                dispatch({
                    type: "SEARCH_LOADING",
                });
                const start = new Date().getTime();

                this.outstanding = new Promise((resolve) => {
                    const serverRequest = server.journal.nutrition
                        .load_search()
                        .then((result) => {
                            if (result.type == "success") {
                                dispatch({
                                    type: "SEARCH_LOADED",
                                    search: result.data,
                                    request_time: new Date().getTime() - start,
                                    from_cache: false,
                                });
                                void hardware.setCache("search-cache", result.data);
                            }
                        });

                    if (cache == null) {
                        void serverRequest.then(resolve);
                    } else {
                        dispatch({
                            type: "SEARCH_LOADED",
                            search: cache,
                            request_time: 0,
                            from_cache: true,
                        });
                        resolve();
                    }
                });
            }

            return this.outstanding;
        };
})();

export const changeSearchFilter = (filter: string) => ({
    type: "CHANGE_SEARCH_FILTER" as const,
    filter,
});

export const changeLastUsedStore = (store: string): Action => ({
    type: "CHANGE_LAST_USED_STORE",
    store: store as TODO_NEEDS_TYPING as keyof Stores,
});

function orderRecent(
    state: RootState,
    staged: {source: string}[],
    recent: SearchResult[],
) {
    if (getProfile(state)?.user.has_beta_access !== true) {
        return recent;
    }

    const selectedPositions = new Map<string, number>();
    staged.forEach((item, index) => {
        selectedPositions.set(item.source, index);
    });
    const selectedIds = new Set(staged.map((entry) => entry.source));

    const relatedList = state.search.foodUser
        .filter((rf) => selectedIds.has(rf.food))
        .flatMap((rf) => rf.related);

    const freqMap = relatedList.reduce<Record<string, number>>((acc, foodId) => {
        acc[foodId] = (acc[foodId] || 0) + 1;
        return acc;
    }, {});

    const sortedRelated = Object.keys(freqMap).sort((a, b) => freqMap[b] - freqMap[a]);
    const top5Related = sortedRelated.slice(0, 5);
    const top5Set = new Set(top5Related.filter((foodId) => !selectedIds.has(foodId)));

    const libraryWithIndex = recent.map((item, idx) => ({item, idx}));

    // Simple lookup to get frequency (0 if not in freqMap)
    const getFreq = (foodId: string) => freqMap[foodId] ?? 0;

    // Sort with custom rules:
    const sorted = libraryWithIndex.sort((a, b) => {
        const aSelected = selectedIds.has(a.item.source);
        const bSelected = selectedIds.has(b.item.source);

        if (aSelected && bSelected) {
            return (
                selectedPositions.get(a.item.source)! -
                selectedPositions.get(b.item.source)!
            );
        }

        // 1) Selected always before unselected
        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;

        // 2) Among unselected, top-5-related come before others
        const aTop5 = top5Set.has(a.item.source);
        const bTop5 = top5Set.has(b.item.source);

        if (aTop5 && !bTop5) return -1;
        if (!aTop5 && bTop5) return 1;

        // 3) If both are in top 5, sort by frequency desc
        if (aTop5 && bTop5) {
            return getFreq(b.item.source) - getFreq(a.item.source);
        }

        // 4) For everything else (both selected, or both neither top 5 nor selected),
        //    preserve original order (stable sort fallback).
        return a.idx - b.idx;
    });

    //
    // 5. Return only the Foods in sorted order
    //
    return sorted.map((wrapped) => wrapped.item);
}

export const getResults = (
    state: RootState,
    storeName: string,
    staged: {source: string}[],
    stores: Omit<Stores, "database">,
) => {
    let results;

    switch (storeName) {
        case "recent": {
            results = orderRecent(state, staged, stores.recent);
            break;
        }
        case "recipes": {
            results = stores.recipes;
            break;
        }
        case "foods": {
            results = stores.foods;
            break;
        }
        // For database searches, do not filter at all. So return.
        case "database":
        default: {
            return state.search.stores.database;
        }
    }
    return results
        .filter((result) => utils.filterPredicate(result.name, state.search.filter))
        .slice(0, 50);
};

export const changeUSDA =
    (value: boolean, keywords: string) => (dispatch: AppDispatch) => {
        dispatch({
            type: "CHANGE_USDA",
            value,
        });

        if (keywords !== "") {
            void dispatch(fetchSearch({keywords}));
        }
    };

export const toggleBulk = () => (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();

    if (state.search.isBulkEnabled) {
        dispatch({
            type: "DISABLE_BULK_MODE",
        });
    } else {
        dispatch({
            type: "ENABLE_BULK_MODE",
        });
    }
};
